import { SOURCE_DEPARTMENT_HEADER } from "@/constants/header";
import { showToast } from "@/providers";
import { getDepartmentFromPathname } from "@/utils/department-routes";
import axios, { AxiosError, AxiosInstance } from "axios";

// Para desenvolvimento (incluindo Lovable), usar path relativo para MSW funcionar
// Para produção, usar VITE_API_URL se definida
const isDev = import.meta.env.DEV;
const isLovable = window.location.hostname.includes("lovable.app") || window.location.hostname.includes("lovable.dev");

// Verificar se modo mock está ativo
const MOCK_MODE_KEY = "vogue-mock-mode";
const unauthenticatedPaths = new Set(["/login", "/atualizar-dados"]);

export function shouldRedirectToLogin(pathname: string): boolean {
  return !unauthenticatedPaths.has(pathname);
}

// Função para obter a baseURL dinamicamente
const getBaseURL = (): string => {
  const isMockMode = localStorage.getItem(MOCK_MODE_KEY) === "true";
  // Se modo mock estiver ativo, usar baseURL vazia para MSW funcionar
  // Caso contrário, usar VITE_API_URL ou fallback
  return (isDev || isLovable) && isMockMode ? "" : import.meta.env.VITE_API_URL || "http://localhost:8080";
};

export const api: AxiosInstance = axios.create({
  headers: {
    "Content-Type": "application/json",
  },
  timeout: 30000,
  withCredentials: true,
});

// Request interceptor
api.interceptors.request.use(
  (config) => {
    // Definir baseURL dinamicamente antes de cada request
    config.baseURL = getBaseURL();

    // Em modo mock com baseURL vazia, garantir que URLs começam com /
    const isMockMode = localStorage.getItem(MOCK_MODE_KEY) === "true";
    if (isMockMode && config.baseURL === "" && config.url && !config.url.startsWith("/")) {
      config.url = `/${config.url}`;
    }

    const sourceDepartment = getDepartmentFromPathname(window.location.pathname);

    if (sourceDepartment && config.headers) {
      config.headers[SOURCE_DEPARTMENT_HEADER] = sourceDepartment;
    } else if (config.headers) {
      delete config.headers[SOURCE_DEPARTMENT_HEADER];
    }

    return config;
  },
  (error) => {
    return Promise.reject(error);
  },
);

// Response interceptor
api.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const shouldSuppressGlobalErrorToast = error.config?.suppressGlobalErrorToast;

    if (error.response?.status === 401) {
      const currentPath = window.location.pathname;
      if (shouldRedirectToLogin(currentPath)) {
        window.location.href = "/login";
        showToast({ type: "error", title: "Sessão expirada. Faça login novamente." });
      }
    } else if (error.response?.status === 403 && !shouldSuppressGlobalErrorToast) {
      showToast({ type: "error", title: "Você não tem permissão para realizar esta ação." });
    } else if (error.response?.status === 500 && !shouldSuppressGlobalErrorToast) {
      showToast({ type: "error", title: "Erro interno do servidor. Tente novamente mais tarde." });
    }

    return Promise.reject(error);
  },
);

export default api;
